import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  Loader,
  Container,
  Header,
  Button,
  Icon,
} from "semantic-ui-react";
import {
  useGetFeedbackLogsQuery,
  useCreateFeedbackLogMutation,
  useDeleteFeedbackLogMutation,
} from "./FeedbackApiSlice";
import { useFetchChatSessionByIdQuery } from "../chat_sessions/chatSessionApiSlice";
import { PAGE_SIZE } from "../../constants";
import "./Feedback.css";

const FeedbackPerform = () => {
  const feedbackRefs = useRef([]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const feedback_session_id = queryParams.get("feedback_session_id");
  const chat_session_id = queryParams.get("chat_session_id");
  const [ isEdit, setIsEdit ] = useState(queryParams.get("edit", false) === "true");
  if (feedback_session_id === null) {
    throw new Error("Session ID not passed from query parameter.");
  }

  // Define API using function
  const [deleteFeedbackLogMutation] = useDeleteFeedbackLogMutation();
  const [createFeedbackLogMutation] = useCreateFeedbackLogMutation();

  // Get session data
  const [sessionLogs, setSessionLogs] = useState([]);
  const {data: feedbackLogs, isSuccess:feedbackIsSuccess, isLoading: feedbackIsLoading, isError: feedbackIsError, error: feedbackError}
    = useGetFeedbackLogsQuery({session: feedback_session_id});
  const {data: chatSession, isSuccess: chatIsSuccess, isLoading: chatIsLoading, isError: chatIsError, error: chatError} 
    = useFetchChatSessionByIdQuery(chat_session_id);

  const isSuccess = feedbackIsSuccess && (chatIsSuccess || chat_session_id === null);
  const isLoading = feedbackIsLoading && chatIsLoading;
  const isError = feedbackIsError && (chatIsError || chat_session_id === null);

  useEffect(() => {
    if (isSuccess) {
      if (isEdit && feedbackLogs.results.length !== 0) {
        setSessionLogs(feedbackLogs.results);
      } else {
        setIsEdit(false);
        setSessionLogs(chatSession.chatlog_set);
      }
    }
  }, [feedbackLogs, chatSession, isSuccess]);

  // handle button click
  const navigate = useNavigate();
  const [isContentUpdated, setIsContentUpdated] = useState(false);

  const handleClickAddBelow = (index, log_speaker) => {
    const newLog = [
      {
        id: uuidv4(),
        speaker: log_speaker === "H" ? "B" : "H",
        utterance: "",
        edited_utterance: "",
        new_row: true,
      },
      {
        id: uuidv4(),
        speaker: log_speaker,
        utterance: "",
        edited_utterance: "",
        new_row: true,
      },
    ];

    setSessionLogs((prevLogs) => {
      const updatedLogs = [...prevLogs];
      updatedLogs.splice(index + 1, 0, ...newLog);
      return updatedLogs;
    });
  };

  const handleClickDelete = (index) => {
    feedbackRefs.current[index].classList.add("delete");
    setIsContentUpdated(!isContentUpdated);
  };

  const handleClickCancel = (index) => {
    feedbackRefs.current[index].classList.remove("delete");
    setIsContentUpdated(!isContentUpdated);
  };

  const handleClickConfirm = () => {
    // Delete existing data
    if (isEdit) {
      sessionLogs.forEach((log) => {
        if (!log.new_row) {
          deleteFeedbackLogMutation(log.id);
        }
      });
    }

    // Define request
    const feedback_logs = sessionLogs
      .filter((log, index) => !feedbackRefs.current[index]?.classList.contains("delete") && (isEdit ? log.edited_utterance : log.utterance) !== "")
      .map((log, index) => ({
        session: feedback_session_id,
        speaker: log.speaker,
        edited_utterance: isEdit ? log.edited_utterance : log.utterance,
        original_chatlog: log.new_row ? null : (isEdit ? log.original_chatlog : log.id),
      }));

    // Send request
    createFeedbackLogMutation({
      feedback_logs: feedback_logs
    });

    // Return to Feedback list page
    navigate(`/feedbackslist?page=1&page_size=${PAGE_SIZE}`);
  };

  let content;
  if (isLoading) {
    content = <Loader active />;
  } else if (sessionLogs.length > 0) {
    content = (
      <>
        <Container>
          <div className="feedback-list">
            {sessionLogs.map((log, index) => (
              <div
                key={log.id}
                className="feedback-row"
                ref={(ref) => (feedbackRefs.current[index] = ref)}
              >
                <div className={`feedback-bubble ${log.speaker === "H" ? "sent" : "received"}`}>
                  <textarea
                    className={`feedback-message ${log.speaker === "H" ? "sent" : "received"}`}
                    value={isEdit ? log.edited_utterance : log.utterance}
                    onChange={(e) => {
                      const updatedLogs = sessionLogs.map((item, i) => {
                        if (i === index) {
                          return { ...item, [isEdit ? "edited_utterance" : "utterance"]: e.target.value,};
                        }
                        return item;
                      });
                      setSessionLogs(updatedLogs);
                    }}
                  />
                </div>

                <div className="feedback-buttons">
                  {feedbackRefs.current[index]?.classList.contains("delete") ? (
                    <Button
                      onClick={() => handleClickCancel(index)}
                      circular color="blue" size="small" className="cancel-button"
                    >
                      CANCEL
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleClickAddBelow(index, log.speaker)}
                        circular color="blue" size="small"
                        style={{ marginBottom: "3px" }}
                      >
                        ADD BELOW
                      </Button>

                      <Button
                        onClick={() => handleClickDelete(index)}
                        circular color="blue" size="small"
                      >
                        DELETE
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>

        <Button
          onClick={handleClickConfirm}
          circular icon color="blue" size="huge" content="send"
          className="confirm-button"
        >
          <Icon name="check" />
        </Button>
      </>
    );
  } else if (sessionLogs.length === 0) {
    content = (
      <Header icon>
        There are no Feedback logs in this session.
      </Header>
    );
  } else if (isError) {
    content = null;
  }

  return content;
};

export default FeedbackPerform;
